import { useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

export type ViewMode = 'density' | 'repFirm' | 'salesRep';

export interface TerritoryOwner {
  id: string;
  name: string;
  stateCodes: string[];
  color: string;
}

interface UsStateMapProps {
  mode: ViewMode;
  // Density mode
  countsByState: Record<string, number>;
  // Territory mode
  owners?: TerritoryOwner[];
  // Selection
  selectedState: string;
  onStateSelect: (stateCode: string) => void;
}

// us-atlas states-10m.json identifies states by FIPS numeric IDs (as strings).
const FIPS_TO_CODE: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT',
  '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL',
  '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
  '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE',
  '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY',
};

const STATE_NAME_TO_CODE: Record<string, string> = {
  ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA',
  COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE', FLORIDA: 'FL', GEORGIA: 'GA',
  HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA',
  KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD',
  MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN', MISSISSIPPI: 'MS', MISSOURI: 'MO',
  MONTANA: 'MT', NEBRASKA: 'NE', NEVADA: 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND',
  OHIO: 'OH', OKLAHOMA: 'OK', OREGON: 'OR', PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC', 'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT',
  VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA', 'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI', WYOMING: 'WY', 'DISTRICT OF COLUMBIA': 'DC',
  TEXA: 'TX',
};

export function normalizeStateCode(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = String(raw).trim().toUpperCase();
  if (!trimmed) return '';
  if (trimmed.length === 2 && /^[A-Z]{2}$/.test(trimmed)) return trimmed;
  return STATE_NAME_TO_CODE[trimmed] || '';
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'D.C.',
};

// Palette for territory owners — visually distinct, decent contrast on white.
// Used by DealersPage to assign deterministic colors by index.
export const OWNER_COLOR_PALETTE = [
  '#2563eb', // blue-600
  '#dc2626', // red-600
  '#16a34a', // green-600
  '#9333ea', // purple-600
  '#ea580c', // orange-600
  '#0891b2', // cyan-600
  '#db2777', // pink-600
  '#65a30d', // lime-600
  '#7c3aed', // violet-600
  '#0d9488', // teal-600
  '#ca8a04', // yellow-600
  '#475569', // slate-600
];

const UNCLAIMED_FILL = '#e5e7eb';      // gray-200
const OVERLAP_FILL = '#f59e0b';        // amber-500 — used when 2+ owners claim a state

function densityFill(count: number, max: number, selected: boolean): string {
  if (selected) return '#1d4ed8';
  if (count === 0 || max === 0) return UNCLAIMED_FILL;
  const intensity = count / max;
  if (intensity > 0.75) return '#b91c1c';
  if (intensity > 0.5)  return '#ea580c';
  if (intensity > 0.25) return '#f59e0b';
  return '#facc15';
}

export default function UsStateMap({
  mode, countsByState, owners = [], selectedState, onStateSelect,
}: UsStateMapProps) {
  const [hover, setHover] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const max = useMemo(() => Math.max(0, ...Object.values(countsByState)), [countsByState]);
  const total = useMemo(() => Object.values(countsByState).reduce((a, b) => a + b, 0), [countsByState]);
  const statesCovered = useMemo(() => Object.values(countsByState).filter(c => c > 0).length, [countsByState]);

  // For territory modes: build state → owner(s) index
  const ownersByState = useMemo(() => {
    const map: Record<string, TerritoryOwner[]> = {};
    for (const owner of owners) {
      for (const code of owner.stateCodes) {
        (map[code] ||= []).push(owner);
      }
    }
    return map;
  }, [owners]);

  function fillForState(code: string): string {
    const isSelected = selectedState === code;
    if (mode === 'density') {
      return densityFill(countsByState[code] || 0, max, isSelected);
    }
    if (isSelected) return '#1d4ed8';
    const stateOwners = ownersByState[code] || [];
    if (stateOwners.length === 0) return UNCLAIMED_FILL;
    if (stateOwners.length === 1) return stateOwners[0].color;
    return OVERLAP_FILL;
  }

  const hoverState = hover || selectedState;
  const hoverCount = hoverState ? (countsByState[hoverState] || 0) : 0;
  const hoverOwners = hoverState ? (ownersByState[hoverState] || []) : [];

  return (
    <div className="relative">
      <div className="w-full" style={{ aspectRatio: '16 / 10', maxHeight: 460 }}>
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 1000 }}
          width={960}
          height={600}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography="/us-states-10m.json">
            {({ geographies }) =>
              geographies.map((geo: any) => {
                const fips = String(geo.id).padStart(2, '0');
                const code = FIPS_TO_CODE[fips];
                if (!code) return null;
                const isSelected = selectedState === code;
                const fill = fillForState(code);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => setHover(code)}
                    onMouseMove={(evt: any) => {
                      const containerRect = (evt.currentTarget as SVGElement)
                        .closest('.relative')
                        ?.getBoundingClientRect();
                      if (containerRect) {
                        setTooltipPos({
                          x: evt.clientX - containerRect.left + 12,
                          y: evt.clientY - containerRect.top + 12,
                        });
                      }
                    }}
                    onMouseLeave={() => { setHover(null); setTooltipPos(null); }}
                    onClick={() => onStateSelect(isSelected ? '' : code)}
                    style={{
                      default:  { fill, stroke: '#ffffff', strokeWidth: 0.9, outline: 'none', cursor: 'pointer' },
                      hover:    { fill: isSelected ? '#1e40af' : '#fde047', stroke: '#1f2937', strokeWidth: 1.25, outline: 'none', cursor: 'pointer' },
                      pressed:  { fill: '#1e40af', stroke: '#ffffff', strokeWidth: 1.25, outline: 'none' },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      {/* Floating tooltip */}
      {hover && tooltipPos && (
        <div
          className="absolute pointer-events-none bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
        >
          <strong>{STATE_NAMES[hover]}</strong>: {countsByState[hover] || 0} dealer{(countsByState[hover] || 0) === 1 ? '' : 's'}
          {mode !== 'density' && (
            <div className="mt-0.5 text-[10px] opacity-90">
              {(ownersByState[hover] || []).length === 0
                ? <span>Unclaimed</span>
                : (ownersByState[hover] || []).map(o => o.name).join(' + ')}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2 mt-2">
        <div className="flex items-center gap-3">
          <span><strong className="text-gray-700">{total}</strong> dealers in <strong className="text-gray-700">{statesCovered}</strong> states</span>
          {hoverState && (
            <span className="text-gray-700">· {STATE_NAMES[hoverState]}: <strong>{hoverCount}</strong></span>
          )}
        </div>

        {/* Legend swaps based on mode */}
        {mode === 'density' ? (
          <div className="flex items-center gap-2">
            <span>Density:</span>
            <div className="flex items-center gap-1">
              <span className="w-4 h-3 rounded" style={{ background: '#e5e7eb', border: '1px solid #d1d5db' }} />
              <span className="w-4 h-3 rounded" style={{ background: '#facc15' }} />
              <span className="w-4 h-3 rounded" style={{ background: '#f59e0b' }} />
              <span className="w-4 h-3 rounded" style={{ background: '#ea580c' }} />
              <span className="w-4 h-3 rounded" style={{ background: '#b91c1c' }} />
            </div>
            <span>0 → {max}</span>
            {selectedState && (
              <button onClick={() => onStateSelect('')} className="ml-2 text-blue-600 hover:text-blue-700 font-medium">
                Clear state filter
              </button>
            )}
          </div>
        ) : (
          selectedState && (
            <button onClick={() => onStateSelect('')} className="text-blue-600 hover:text-blue-700 font-medium">
              Clear state filter
            </button>
          )
        )}
      </div>

      {/* Territory owner legend (rep firms / sales reps) */}
      {mode !== 'density' && owners.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            {owners.map(owner => (
              <div key={owner.id} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ background: owner.color }} />
                <span className="text-gray-700">{owner.name}</span>
                <span className="text-gray-400">({owner.stateCodes.length})</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded border border-gray-300" style={{ background: UNCLAIMED_FILL }} />
              <span className="text-gray-500">Unclaimed</span>
            </div>
            {Object.values(ownersByState).some(arr => arr.length > 1) && (
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ background: OVERLAP_FILL }} />
                <span className="text-gray-500">Overlap (2+ owners)</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
